import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Package, Download, Check, Loader2, ChevronLeft, Crown, Scale, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCheckoutSession } from '../services/paymentService';
import { HorizontalScroll } from '../components/HorizontalScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BundleDetail = () => {
  const { id } = useParams();
  const [bundle, setBundle] = useState(null);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [relatedBundles, setRelatedBundles] = useState([]);

  useEffect(() => {
    const fetchBundle = async () => {
      try {
        const resp = await axios.get(`${API}/bundle/${id}`);
        setBundle(resp.data.bundle);
        setGuides(resp.data.guides || []);

        // Fetch related bundles from same category for cross-sell
        if (resp.data.bundle?.category) {
          const allBundles = await axios.get(`${API}/bundles`);
          const sameCat = (allBundles.data.bundles || []).filter(
            b => b.category === resp.data.bundle.category && b.id !== id
          );
          setRelatedBundles(sameCat.slice(0, 4));
        }
      } catch (err) {
        console.error('Error fetching bundle:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBundle();
  }, [id]);

  const handlePurchase = async (productId) => {
    try {
      setPurchasing(true);
      const session = await createCheckoutSession(productId || bundle.id);
      if (session.url) window.location.href = session.url;
    } catch {
      alert('Unable to process purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="bundle-detail-loading">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!bundle || bundle.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Bundle not found</h2>
          <Button onClick={() => window.location.href = '/bundles'} data-testid="back-to-bundles-btn">Back to Bundles</Button>
        </div>
      </div>
    );
  }

  const isVault = bundle.product_type === 'vault';
  const isLawsuit = bundle.product_type === 'bundle_lawsuit';
  const isSystem = bundle.product_type === 'bundle_category';
  const isAdvanced = bundle.product_type === 'bundle_10';

  const tierLabel = bundle.tier_label || (isSystem ? 'Popular' : isAdvanced ? 'Trending' : 'Best Value');
  const savingsLabel = bundle.savings_label || '';

  const borderColor = isSystem ? 'border-orange-500' : isAdvanced ? 'border-purple-400' : isVault ? 'border-yellow-400' : 'border-blue-400';
  const accentColor = isSystem ? 'text-orange-600' : isAdvanced ? 'text-purple-600' : isVault ? 'text-yellow-500' : 'text-blue-600';
  const gradient = isSystem ? 'from-orange-500 to-red-500' : isAdvanced ? 'from-purple-500 to-violet-500' : isVault ? 'from-gray-800 to-gray-900' : isLawsuit ? 'from-emerald-600 to-teal-500' : 'from-blue-500 to-indigo-500';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* 1. Title + Benefit Summary */}
        <div className="text-center mb-8" data-testid="bundle-header">
          <div className="inline-flex items-center gap-2 mb-3">
            {tierLabel && (
              <span className={`${isSystem ? 'bg-orange-600' : isAdvanced ? 'bg-purple-600' : 'bg-blue-600'} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                {tierLabel}
              </span>
            )}
            {savingsLabel && (
              <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                {savingsLabel}
              </span>
            )}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-testid="bundle-title">
            {bundle.title}
          </h1>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto" data-testid="bundle-description">
            {bundle.standard_description
              ? bundle.standard_description.split('\n')[0]
              : bundle.description}
          </p>
        </div>

        {/* 2. Main Purchase Card — What's Included + Buy */}
        <div className={`bg-white rounded-2xl shadow-xl overflow-visible ${borderColor} border-2 mb-8 relative`} data-testid="bundle-detail-card">
          {/* Badge on the card, not the book */}
          {getBadge(bundle) && (() => {
            const badgeKey = getBadge(bundle);
            const badgeMap = { trending: { bg: 'bg-red-500', emoji: '🔥', label: 'Trending' }, fast: { bg: 'bg-amber-500', emoji: '⚡', label: 'Fast Results' }, downloaded: { bg: 'bg-blue-500', emoji: '⭐', label: 'Most Downloaded' }, beginner: { bg: 'bg-orange-500', emoji: '🧠', label: 'Beginner Friendly' }, value: { bg: 'bg-green-500', emoji: '💰', label: 'Best Value' } };
            const b = badgeMap[badgeKey];
            return b ? <div className={`absolute top-3 left-3 z-20 ${b.bg} text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow-lg flex items-center gap-1`}><span>{b.emoji}</span><span>{b.label}</span></div> : null;
          })()}
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative min-h-[320px] flex items-center justify-center bg-white rounded-l-2xl overflow-hidden">
              <BookMockup productId={bundle.id} title={bundle.title} size="xl" />
              <div className={`absolute top-4 right-4 z-20 ${isVault ? 'bg-yellow-400 text-gray-900' : 'bg-white text-gray-900'} font-bold text-xl px-5 py-2 rounded-full shadow-lg`}>
                ${bundle.price}
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="mb-4">
                <span className={`text-sm font-semibold ${accentColor} px-3 py-1 rounded bg-gray-50`} data-testid="bundle-type-badge">
                  {bundle.guide_count || guides.length} Guides Included
                </span>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-5 mb-6">
                <h3 className="font-bold text-gray-900 mb-3">What's Included:</h3>
                <ul className="space-y-2">
                  {(bundle.bullets || [
                    'Step-by-step legal templates ready to use',
                    'Covers real-life situations with proven formats',
                    'Includes editable documents and instructions',
                    'Designed for fast action and results',
                    'Suitable for personal or business use',
                  ]).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600 shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <Button
                  className={`w-full bg-gradient-to-r ${gradient} text-white text-lg py-6 shadow-lg hover:opacity-90`}
                  onClick={() => handlePurchase(bundle.id)}
                  disabled={purchasing}
                  data-testid="bundle-buy-btn"
                >
                  {purchasing ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <>Start Fixing This Today — ${bundle.price}</>
                  )}
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className={`text-xl font-bold ${accentColor}`}>{bundle.guide_count || guides.length}</div>
                    <div className="text-xs text-gray-600">Guides</div>
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${accentColor}`}>PDF</div>
                    <div className="text-xs text-gray-600">Format</div>
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${accentColor}`}>24/7</div>
                    <div className="text-xs text-gray-600">Access</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Trust Section — "Why These Templates Work" */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 md:p-8 mb-8" data-testid="trust-section">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-green-600" /> Why These Templates Work
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Built using structured formats similar to real legal and financial documents',
              'Designed for real-world situations, not generic examples',
              'Step-by-step layout so users know exactly what to do',
              'Clear and organized — no confusion',
              'Created to help users take action immediately',
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 4. Comparison Table — "Choose the Right Option for You" */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 md:p-8 mb-8" data-testid="comparison-table">
          <h2 className="text-xl font-bold text-gray-900 mb-5 text-center">Choose the Right Option for You</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-3 text-gray-600 font-semibold">Feature</th>
                  <th className="text-center py-3 px-3 text-gray-900 font-semibold">Single Guide</th>
                  <th className="text-center py-3 px-3 text-blue-700 font-semibold bg-blue-50/50">Bundle (5)</th>
                  <th className="text-center py-3 px-3 text-purple-700 font-semibold">Advanced ($89)</th>
                  <th className="text-center py-3 px-3 text-orange-700 font-semibold bg-orange-50/50">Full System ($150)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3 text-gray-700 font-medium">Number of Templates</td>
                  <td className="py-3 px-3 text-center text-gray-600">1</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">5</td>
                  <td className="py-3 px-3 text-center text-gray-600">10–15</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Complete Access</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3 text-gray-700 font-medium">Instructions Included</td>
                  <td className="py-3 px-3 text-center text-gray-600">Detailed</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">Detailed</td>
                  <td className="py-3 px-3 text-center text-gray-600">Advanced Step-by-Step</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Full System Guidance</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-3 text-gray-700 font-medium">Coverage</td>
                  <td className="py-3 px-3 text-center text-gray-600">One Issue</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">Multiple Issues</td>
                  <td className="py-3 px-3 text-center text-gray-600">Expanded Coverage</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Complete Solution</td>
                </tr>
                <tr>
                  <td className="py-3 px-3 text-gray-700 font-medium">Best For</td>
                  <td className="py-3 px-3 text-center text-gray-600">Quick Fix</td>
                  <td className="py-3 px-3 text-center text-gray-600 bg-blue-50/30">Most Users</td>
                  <td className="py-3 px-3 text-center text-gray-600">Serious Users</td>
                  <td className="py-3 px-3 text-center font-semibold text-orange-700 bg-orange-50/30">Full Control &amp; Maximum Results</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 5. Included Guides List */}
        {guides.length > 0 && (
          <div className="mb-8" data-testid="included-guides-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              All {guides.length} Guides in This Bundle
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {guides.map((guide, idx) => (
                <a
                  key={guide.id}
                  href={`/guide/${guide.id}`}
                  className="bg-white rounded-xl shadow-md border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all p-4 flex items-start gap-3 group"
                  data-testid={`included-guide-${guide.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                    {idx + 1}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2">
                      {guide.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">{guide.subcategory}</p>
                    <p className="text-xs text-gray-400 mt-0.5">${guide.price} individually</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* 6. Cross-sell: People also bought — Horizontal Swipe */}
        {relatedBundles.length > 0 && (
          <div className="mb-8" data-testid="cross-sell-bundles">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">People also bought</h2>
            <HorizontalScroll>
              {relatedBundles.map((item) => (
                <a
                  key={item.id}
                  href={`/bundle/${item.id}`}
                  className="snap-start shrink-0 w-[200px] bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all border border-gray-200 hover:border-orange-300 block"
                  data-testid={`cross-sell-bundle-${item.id}`}
                >
                  <BookMockup productId={item.id} title={item.title} size="card" badge={getBadge(item)} />
                  <div className="p-3">
                    <h3 className="text-xs font-bold text-gray-900 line-clamp-2 text-center">{item.title}</h3>
                    {item.savings_label && (
                      <p className="text-[10px] text-green-600 font-semibold text-center mt-1">{item.savings_label}</p>
                    )}
                  </div>
                </a>
              ))}
            </HorizontalScroll>
          </div>
        )}

        {/* 7. Post-Purchase Upsell */}
        {!isVault && !isSystem && (
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 text-white text-center" data-testid="post-purchase-upsell">
            <h2 className="text-xl font-bold mb-2">Upgrade and get everything</h2>
            <p className="text-gray-400 text-sm mb-5">Get the full system for maximum coverage and results</p>
            <Button
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 text-sm px-8"
              onClick={() => window.location.href = '/bundles'}
              data-testid="upsell-full-system-btn"
            >
              View Full Systems — $150 <ArrowRight size={14} className="ml-1" />
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BundleDetail;
